import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/store';

import { lumioContextEvent, lumioNudgeEvent, LumioNudge } from './components/LumioChat/endpoint';
import { lumioInactiveFeatures } from '../../lib/lumioSignals';
import { useLumioSignals } from './useLumioSignals';

/**
 * One shared delivery path for page signals and backend event facts. Navigation,
 * casino and locale changes trigger immediately. Visible pages refresh backend
 * facts once a minute; changed signals use a 20s floor. Signals are canonical
 * facts (see `useLumioSignals`), so delivery is identical in every language.
 *
 * The backend owns frequency/session limits and occurrence-lifetime dedupe.
 * A shown nudge resolves once: click, dismissal, navigation or expiry. Upcoming
 * notices expire at their start time; they never turn into unverified live claims.
 */

const POLL_MS = 4000; // re-check the page this often while it's mounted
const FIRST_CHECK_MS = 800; // a quick first look after navigation settles
const BACKEND_REFRESH_MS = 60_000;
const MIN_GAP_MS = 20_000; // floor between sends (the backend is the real throttle)

type Outcome = 'clicked' | 'dismissed' | 'ignored';

export interface UseLumioNudges {
  nudge: LumioNudge | null;
  /** Record the owner's response and clear the nudge. */
  resolve: (outcome: Outcome) => void;
}

/**
 * Evaluate visible page context and retire notices when their scope changes.
 *
 * `enabled` must mean "a surface can show a nudge right now", because asking is
 * the only thing that costs anything. The backend spends a notice when it EMITS
 * it: the dedupe key, the 180s gap and one of the three per-session slots are
 * written before the response is sent (nudges.py:742-833), and no outcome we
 * report afterwards gives any of it back (record_outcome returns early for every
 * id but `setup_incomplete`). So a nudge we request and cannot show is simply
 * lost, and reporting or not reporting it changes nothing.
 *
 * The caller's job is therefore to keep a surface available rather than to gate
 * narrowly: see `MainLayout`, where the toast floats whenever no panel can dock,
 * so there is almost always somewhere for a notice to land.
 */
export const useLumioNudges = (enabled = true): UseLumioNudges => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const locale = (i18n.language || 'en').split('-')[0];
  const casino = useAppSelector((state) => state.casinoInstance.displayBrand?.id ?? '');
  const [nudge, setNudge] = useState<LumioNudge | null>(null);

  const lastEvalAt = useRef<number>(0);
  // Read at tick time rather than held as an effect dependency. `enabled` follows
  // the surfaces, so it flips on ordinary UI actions (opening the overlay, the
  // inbox, starting a conversation). As a dependency it tore the effect down and
  // aborted whatever evaluation was in flight, and the backend had already
  // written that nudge off: emitted, deduped, one session slot gone. Keeping the
  // effect alive across surface changes means an answer we already paid for still
  // lands.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const lastSent = useRef<string>(''); // page|casino|signals of the last event we sent
  // Read at send time through a ref, so a score change never restarts the polling effect.
  const storeSignals = useLumioSignals();
  const signalsRef = useRef(storeSignals);
  signalsRef.current = storeSignals;
  const nudgeRef = useRef<LumioNudge | null>(null);
  nudgeRef.current = nudge;

  const resolve = useCallback((outcome: Outcome) => {
    const current = nudgeRef.current;
    nudgeRef.current = null;
    setNudge(null);
    if (current) lumioNudgeEvent(current, outcome);
  }, []);

  // Retire a live notice when its SCOPE changes: route, brand or language. The
  // notice was evaluated against a page the owner has since left, so it should
  // not outlive it.
  //
  // Deliberately NOT keyed on `enabled`. A surface closing (the owner opens the
  // proposals inbox, or starts a conversation in the docked panel) flips
  // `enabled` too, and reporting `ignored` there would tell the backend the
  // owner passed on a notice they actually saw, which costs them the next one.
  useEffect(() => {
    resolve('ignored');
  }, [locale, location.pathname, casino, resolve]);

  useEffect(() => {
    // This effect is keyed on the SCOPE alone (route, brand, language), so its
    // re-run means the scope changed, which is exactly what earns an immediate
    // look past the 20s floor and the dedupe check.
    let initial = true;
    let inFlight = false;
    const controller = new AbortController();

    const tick = (): void => {
      // Expiry is housekeeping, not a request, so it runs even with no surface:
      // an upcoming notice whose occurrence has started must never be shown as a
      // live claim, whatever the chrome is doing.
      const current = nudgeRef.current;
      if (current?.expires_at && Date.parse(current.expires_at) <= Date.now()) resolve('ignored');
      // Nothing can show a notice right now, so do not ask for one: the backend
      // spends it on emit and nothing we report gives it back.
      if (!enabledRef.current) return;
      if (nudgeRef.current || inFlight || document.hidden) return;
      // No brand yet (the brands fetch has not landed): evaluating now would ask
      // the backend about casino_instance_id '', and it would answer with a
      // notice scoped to no casino, charged to the wrong per-brand bucket.
      if (!casino) return;
      // Freeze the selected casino with this effect and its cancellation scope.
      const context = { page: window.location.pathname, casino_instance_id: casino };
      const signals = { ...signalsRef.current, inactive_features: lumioInactiveFeatures() };
      const sig = `${context.page}|${casino}|${JSON.stringify(signals)}`;
      const elapsed = Date.now() - lastEvalAt.current;
      if (!initial && elapsed < MIN_GAP_MS) return;
      if (!initial && sig === lastSent.current && elapsed < BACKEND_REFRESH_MS) return;
      initial = false;
      inFlight = true;
      lastSent.current = sig;
      lastEvalAt.current = Date.now();
      lumioContextEvent(context, signals, locale, controller.signal)
        .then((result) => {
          if (controller.signal.aborted || !result || nudgeRef.current) return;
          if (result.expires_at && Date.parse(result.expires_at) <= Date.now()) return;
          nudgeRef.current = result;
          setNudge(result);
        })
        .catch((error: unknown) => {
          if (!controller.signal.aborted) console.error('Lumio nudge evaluation failed', error);
        })
        .finally(() => {
          inFlight = false;
        });
    };

    // A prompt first look after the route settles, then a steady re-check that
    // catches late-loading data and later changes.
    const first = window.setTimeout(tick, FIRST_CHECK_MS);
    const interval = window.setInterval(tick, POLL_MS);
    return () => {
      controller.abort();
      window.clearTimeout(first);
      window.clearInterval(interval);
    };
  }, [locale, location.pathname, casino, resolve]);

  return { nudge, resolve };
};
